import React, { useEffect, useState } from "react";
import { Card, Empty, Space, Spin, Alert, Typography, Tag, Row, Col, Pagination } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { usePushResendCallbacks } from "../../hooks/usePushResendCallbacks";
import PageHeader from "../../components/common/PageHeader";

const { Text } = Typography;

/**
 * Página de monitoramento de reenvio de callbacks do Push.
 *
 * Exibe todos os IDs que tiveram callbacks reenviados em formato de cards com paginação.
 */
const PushResendCallbacksMonitoringContent: React.FC = () => {
  const { cnjIds, results, resendCallbacks, loading, error, setResults } = usePushResendCallbacks();
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  const [processedCnjIdsKey, setProcessedCnjIdsKey] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  /**
   * Cria uma chave única baseada nos IDs para detectar quando os IDs mudam
   */
  const cnjIdsKey = cnjIds.join(",");

  /**
   * Efeito para executar o reenvio de callbacks quando os IDs mudam.
   */
  useEffect(() => {
    const executeResend = async () => {
      // Se não há IDs, não faz nada
      if (cnjIds.length === 0) {
        return;
      }

      // Se os IDs mudaram, limpa resultados anteriores e executa novo reenvio
      if (cnjIdsKey !== processedCnjIdsKey) {
        setResults(null); // Limpa resultados anteriores
        setProcessedCnjIdsKey(cnjIdsKey); // Atualiza a chave processada

        setInitialLoading(true);
        try {
          await resendCallbacks(cnjIds);
        } catch (err) {
          console.error("Erro ao reenviar callbacks:", err);
        } finally {
          setInitialLoading(false);
        }
      }
    };

    executeResend();
  }, [cnjIds, cnjIdsKey, processedCnjIdsKey, resendCallbacks, setResults]);

  // Resetar página quando os resultados mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [results]);

  if (initialLoading || loading) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Reenvio de Callbacks" />
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "40px" }} />
        </Space>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Reenvio de Callbacks" />
          <Alert message="Erro ao reenviar callbacks" description={error} type="error" showIcon />
        </Space>
      </Card>
    );
  }

  if (cnjIds.length === 0) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Reenvio de Callbacks" />
          <Empty description="Nenhum ID para reenviar callbacks. Volte para a página de Reenviar Callbacks para adicionar IDs." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Space>
      </Card>
    );
  }

  if (!results || !results.data || results.data.length === 0) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <PageHeader title="Monitoramento de Reenvio de Callbacks" />
          <Empty description="Nenhum resultado disponível." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Space>
      </Card>
    );
  }

  // Calcular itens da página atual
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentItems = results.data.slice(startIndex, endIndex);
  const total = results.data.length;

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <PageHeader title="Monitoramento de Reenvio de Callbacks" />
        <div>
          <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
            Total de IDs processados: {total}
          </Text>
          <Row gutter={[16, 16]}>
            {currentItems.map((id, index) => (
              <Col key={`${id}-${index}`} xs={24} sm={24} md={24} lg={24} xl={24}>
                <Card size="small">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: "8px" }}>
                    <div style={{ flex: "1", minWidth: "200px" }}>
                      <Text strong style={{ marginRight: 8 }}>ID:</Text>
                      <Text code copyable style={{ fontSize: "14px" }}>
                        {id}
                      </Text>
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <Text strong style={{ marginRight: 8 }}>Status:</Text>
                      <Tag color="success" icon={<CheckCircleOutlined />}>
                        Enviado
                      </Tag>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} IDs`}
              pageSizeOptions={["25", "50", "100", "200", "500"]}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              onShowSizeChange={(current, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }}
            />
          </div>
        </div>
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página PushResendCallbacksMonitoring.
 */
const PushResendCallbacksMonitoring: React.FC = () => {
  return (
    <PushResendCallbacksMonitoringContent />
  );
};

export default PushResendCallbacksMonitoring;

